/**
 * TransferConversationDialog - Componente compartilhado para transferir conversas
 * Usado em: ChatBox, ConversationCardImproved
 */

import React, { useState, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from '@/shared/icons';
import { useTeamsDropdown } from '@/modules/teams/hooks/useTeams';
import { useUsersDropdown } from '@/modules/users/hooks/useUsers';
import { Avatar, AvatarImage, AvatarFallback } from '@/shared/components/ui/avatar';
import { Input } from '@/shared/components/ui/Input';
import { Textarea } from '@/shared/components/ui/Textarea';

interface Agent {
  id: string;
  name: string;
  email: string;
  avatar?: string;
}

interface Team {
  id: string;
  name: string;
  description?: string;
  memberCount?: number;
}

export interface TransferConversationDialogProps {
  conversationId: string;
  conversationSubject?: string;
  isOpen: boolean;
  onClose: () => void;
  onTransfer: (targetType: 'user' | 'team', targetId: string, notes?: string) => void;
  isLoading?: boolean;
}

export const TransferConversationDialog: React.FC<TransferConversationDialogProps> = ({
  conversationId: _conversationId,
  conversationSubject,
  isOpen,
  onClose,
  onTransfer,
  isLoading = false,
}) => {
  const [transferType, setTransferType] = useState<'user' | 'team'>('user');
  const [selectedTarget, setSelectedTarget] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [notes, setNotes] = useState('');

  // Fetch teams from API
  const { data: teamsData = [] } = useTeamsDropdown();

  // Fetch agents from API
  const { data: usersData = [], isLoading: isLoadingUsers } = useUsersDropdown({ status: 'active' });

  // Transform teams data to component format
  const teams: Team[] = useMemo(() => {
    return teamsData.map(team => ({
      id: team.id,
      name: team.name,
      description: team.description,
      memberCount: undefined // Not available from API
    }));
  }, [teamsData]);

  // Transform users data to agent format
  const agents: Agent[] = useMemo(() => {
    return usersData.map(user => ({
      id: user.id,
      name: user.name,
      email: user.email,
      avatar: user.avatar
    }));
  }, [usersData]);

  const filteredAgents = agents.filter((agent) =>
    agent.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    agent.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredTeams = teams.filter((team) =>
    team.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    team.description?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleTransfer = () => {
    if (!selectedTarget) return;
    onTransfer(transferType, selectedTarget, notes.trim() || undefined);
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      data-testid="chat-transfer-dialog"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)' }}
      /* form: NÃO fecha ao clicar fora (DS §Overlays dismissal) */
    >
      {/* Dialog */}
      <div
        className="relative rounded-t-2xl sm:rounded-lg shadow-xl max-w-screen-sm w-full sm:mx-4 overflow-hidden max-h-[92vh] sm:max-h-[85vh] flex flex-col"
        style={{
          backgroundColor: '#ffffff',
          // Override CSS variables to light theme (modals are always light)
          '--theme-text': '#1e293b',
          '--theme-muted-text': '#6b7280',
          '--theme-input-background': '#ffffff',
          '--theme-input-text': '#1e293b',
          '--theme-input-border': 'rgba(0, 0, 0, 0.10)',
          '--theme-input-focus': '#8b5cf6',
          '--theme-error-color': '#ef4444',
          '--theme-card-background': '#ffffff',
          '--theme-card-text': '#334155',
          '--theme-card-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-card-muted': '#94a3b8',
          '--theme-button-primary': '#8b5cf6',
          '--theme-button-primary-text': '#ffffff',
          '--theme-button-primary-hover': '#7c3aed',
          '--theme-primary': '#8b5cf6',
          '--theme-border': 'rgba(0, 0, 0, 0.06)',
          '--theme-component-background': '#ffffff',
        } as React.CSSProperties}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4 border-b"
          style={{ borderColor: '#e5e7eb' }}
        >
          <div className="flex items-center gap-3">
            <div
              className="w-10 h-10 rounded-full flex items-center justify-center"
              style={{
                backgroundColor: 'rgba(139, 92, 246, 0.1)',
                color: '#8b5cf6',
              }}
            >
              <Icon name="refresh" className="w-5 h-5" />
            </div>
            <div>
              <h3 className="text-lg font-semibold" style={{ color: '#1e293b' }}>
                Transferir conversa
              </h3>
              {conversationSubject && (
                <p className="text-sm" style={{ color: '#64748b' }}>
                  {conversationSubject}
                </p>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            disabled={isLoading}
            className="p-1 rounded transition-colors disabled:opacity-50"
            style={{ color: '#64748b' }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#f1f5f9';
              e.currentTarget.style.color = '#1e293b';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.color = '#64748b';
            }}
          >
            <Icon name="x" className="w-5 h-5" />
          </button>
        </div>

        {/* Transfer Type Tabs */}
        <div className="border-b px-6" style={{ borderColor: '#e5e7eb' }}>
          <div className="flex gap-4">
            <button
              data-testid="chat-transfer-tab-user"
              onClick={() => {
                setTransferType('user');
                setSelectedTarget(null);
              }}
              className="flex items-center gap-2 px-4 py-3 border-b-2 transition-colors"
              style={{
                borderColor: transferType === 'user' ? '#8b5cf6' : 'transparent',
                color: transferType === 'user' ? '#8b5cf6' : '#64748b',
              }}
              onMouseEnter={(e) => {
                if (transferType !== 'user') {
                  e.currentTarget.style.color = '#1e293b';
                }
              }}
              onMouseLeave={(e) => {
                if (transferType !== 'user') {
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              <Icon name="user" className="w-4 h-4" />
              <span className="font-medium">Operador</span>
            </button>
            <button
              data-testid="chat-transfer-tab-team"
              onClick={() => {
                setTransferType('team');
                setSelectedTarget(null);
              }}
              className="flex items-center gap-2 px-4 py-3 border-b-2 transition-colors"
              style={{
                borderColor: transferType === 'team' ? '#8b5cf6' : 'transparent',
                color: transferType === 'team' ? '#8b5cf6' : '#64748b',
              }}
              onMouseEnter={(e) => {
                if (transferType !== 'team') {
                  e.currentTarget.style.color = '#1e293b';
                }
              }}
              onMouseLeave={(e) => {
                if (transferType !== 'team') {
                  e.currentTarget.style.color = '#64748b';
                }
              }}
            >
              <Icon name="users" className="w-4 h-4" />
              <span className="font-medium">Equipe</span>
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-6 py-3 border-b" style={{ borderColor: '#e5e7eb' }}>
          <div className="relative">
            <Icon
              name="search"
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4"
              style={{ color: '#64748b' }}
            />
            <Input
              data-testid="chat-transfer-search"
              type="text"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              placeholder={`Buscar ${transferType === 'user' ? 'operador' : 'equipe'}...`}
              className="w-full pl-10"
              icon={undefined}
            />
          </div>
        </div>

        {/* List */}
        <div className="flex-1 overflow-y-auto px-6 py-3">
          {transferType === 'user' ? (
            <div className="space-y-2">
              {isLoadingUsers ? (
                <div className="text-center py-8">
                  <div className="flex items-center justify-center gap-2" style={{ color: '#64748b' }}>
                    <Icon name="loader" className="w-5 h-5 animate-spin" />
                    <p>Carregando operadores...</p>
                  </div>
                </div>
              ) : filteredAgents.length === 0 ? (
                <div className="text-center py-8">
                  <p style={{ color: '#64748b' }}>
                    {searchTerm ? 'Nenhum operador encontrado' : 'Nenhum operador disponível'}
                  </p>
                </div>
              ) : (
                filteredAgents.map((agent) => (
                  <button
                    key={agent.id}
                    data-testid={`chat-transfer-user-option-${agent.id}`}
                    onClick={() => setSelectedTarget(agent.id)}
                    className="w-full text-left p-4 rounded-lg border-2 transition-all"
                    style={{
                      borderColor: selectedTarget === agent.id ? '#8b5cf6' : '#e5e7eb',
                      backgroundColor: selectedTarget === agent.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTarget !== agent.id) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTarget !== agent.id) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <div
                        className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: selectedTarget === agent.id ? '#8b5cf6' : '#e5e7eb',
                          backgroundColor: selectedTarget === agent.id ? '#8b5cf6' : 'transparent',
                        }}
                      >
                        {selectedTarget === agent.id && <Icon name="check" className="w-3 h-3 text-white" />}
                      </div>

                      {/* Avatar */}
                      <Avatar className="w-10 h-10" isSquircle>
                        <AvatarImage
                          src={agent.avatar}
                          alt={agent.name}
                          isSquircle
                        />
                        <AvatarFallback isSquircle>
                          <span className="text-indigo-600 font-semibold">
                            {agent.name.charAt(0).toUpperCase()}
                          </span>
                        </AvatarFallback>
                      </Avatar>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium truncate" style={{ color: '#1e293b' }}>
                          {agent.name}
                        </h4>
                        <p className="text-sm truncate" style={{ color: '#64748b' }}>
                          {agent.email}
                        </p>
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {filteredTeams.length === 0 ? (
                <div className="text-center py-8">
                  <p style={{ color: '#64748b' }}>
                    {searchTerm ? 'Nenhuma equipe encontrada' : 'Nenhuma equipe disponível'}
                  </p>
                </div>
              ) : (
                filteredTeams.map((team) => (
                  <button
                    key={team.id}
                    data-testid={`chat-transfer-team-option-${team.id}`}
                    onClick={() => setSelectedTarget(team.id)}
                    className="w-full text-left p-4 rounded-lg border-2 transition-all"
                    style={{
                      borderColor: selectedTarget === team.id ? '#8b5cf6' : '#e5e7eb',
                      backgroundColor: selectedTarget === team.id ? 'rgba(139, 92, 246, 0.1)' : 'transparent',
                    }}
                    onMouseEnter={(e) => {
                      if (selectedTarget !== team.id) {
                        e.currentTarget.style.borderColor = '#cbd5e1';
                      }
                    }}
                    onMouseLeave={(e) => {
                      if (selectedTarget !== team.id) {
                        e.currentTarget.style.borderColor = '#e5e7eb';
                      }
                    }}
                  >
                    <div className="flex items-center gap-3">
                      {/* Checkbox */}
                      <div
                        className="w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0"
                        style={{
                          borderColor: selectedTarget === team.id ? '#8b5cf6' : '#e5e7eb',
                          backgroundColor: selectedTarget === team.id ? '#8b5cf6' : 'transparent',
                        }}
                      >
                        {selectedTarget === team.id && <Icon name="check" className="w-3 h-3 text-white" />}
                      </div>

                      {/* Icon */}
                      <div
                        className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                        style={{
                          backgroundColor: 'rgba(139, 92, 246, 0.1)',
                          color: '#8b5cf6',
                        }}
                      >
                        <Icon name="users" className="w-5 h-5" />
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium" style={{ color: '#1e293b' }}>
                          {team.name}
                        </h4>
                        {team.description && (
                          <p className="text-sm" style={{ color: '#64748b' }}>
                            {team.description}
                          </p>
                        )}
                        {team.memberCount !== undefined && (
                          <p className="text-xs mt-0.5" style={{ color: '#64748b' }}>
                            {team.memberCount} {team.memberCount === 1 ? 'membro' : 'membros'}
                          </p>
                        )}
                      </div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>

        {/* Notes */}
        <div className="px-6 py-3 border-t" style={{ borderColor: '#e5e7eb' }}>
          <label
            htmlFor="transfer-notes"
            className="block text-sm font-medium mb-2"
            style={{ color: '#1e293b' }}
          >
            Observações (opcional)
          </label>
          <Textarea
            id="transfer-notes"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Adicione informações relevantes para o próximo atendente..."
            rows={3}
            resize="none"
          />
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4 border-t"
          style={{
            backgroundColor: '#f1f5f9',
            borderColor: '#e5e7eb',
          }}
        >
          <button
            data-testid="chat-transfer-cancel"
            onClick={onClose}
            disabled={isLoading}
            className="px-4 py-2 text-sm font-medium border rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{
              color: '#1e293b',
              backgroundColor: '#ffffff',
              borderColor: '#e5e7eb',
            }}
            onMouseEnter={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#f1f5f9';
              }
            }}
            onMouseLeave={(e) => {
              if (!isLoading) {
                e.currentTarget.style.backgroundColor = '#ffffff';
              }
            }}
          >
            Cancelar
          </button>
          <button
            data-testid="chat-transfer-confirm"
            onClick={handleTransfer}
            disabled={isLoading || !selectedTarget}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#8b5cf6' }}
            onMouseEnter={(e) => {
              if (!isLoading && selectedTarget) {
                e.currentTarget.style.backgroundColor = '#7c3aed';
              }
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = '#8b5cf6';
            }}
          >
            {isLoading ? (
              <>
                <Icon name="loader" className="w-4 h-4 animate-spin" />
                <span>Transferindo...</span>
              </>
            ) : (
              <>
                <Icon name="refresh" className="w-4 h-4" />
                <span>Transferir conversa</span>
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default TransferConversationDialog;
